/**
 * Replacements, inside Manifest Review beside the lead desk (Isaiah Cotto,
 * 2026-09-30).
 *
 * "They cant be assigned as a replacement until its confirmed they are still
 * in warranty and that their initial UMED is paid in full. Also warranty date
 * is located in CE under devices tab."
 *
 * One row per resident waiting for a replacement, grouped by facility, A to
 * Z. Each row carries the two checks a person makes -- warranty, read in
 * CounselEAR's Devices tab, and paid in full -- and the place button stays
 * off until both have passed. The server refuses a placement on the same
 * rule, so the button is a convenience, not the lock.
 *
 * == Why, and who it goes to (EP-010, 2026-10-01) ===========================
 *
 * A replacement says why: side, reason, and how it is paid for, with an
 * amount only if a person types one (nothing here knows a price). Any
 * resident can be added with "Add a replacement"; a row's reason can be
 * recorded or replaced; placing needs one.
 *
 * "When a replacement is made that patient should be reassigned to the PA who
 * is responsible for the facility." So nobody is chosen here. The row says
 * who the replacement goes to -- the PA who covers the resident's facility --
 * and when nobody does, it says so and links to Facility coverage rather than
 * offering anyone else. The previous holder is shown as information.
 *
 * == A board (2026-10-07) ====================================================
 *
 * The same table as the other Manifest Review tabs: one row per resident, short
 * cells, and the row opens DOCKED beside the table with Place, Reason, Checks
 * and Details. Nothing grows inside the table. Every action keeps its call
 * (placeReplacement, requestReplacement, cancelReplacementRequest, recordCheck)
 * and its refusal words. "Add a replacement" is a button in the board head and
 * opens its form there.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MfLoadFailure, MfSkeleton } from './mfStates';
import FilledStatusChip from '../../components/status/FilledStatusChip';
import { UMED_STATUS } from '../../theme/mondayPalette';
import MfBoard, { useClientSort, useOpenParam, type BoardColumn, type BoardIdentity, type BoardDetailSection } from './board/MfBoard';
import MfLegend, { type MfLegendItem } from './board/MfLegend';
import { ResidentName, residentLabel, residentName } from './board/residents';
import { ToneChip } from './board/chips';
import BuildingOrClinic, { buildingWords, clinicWords } from '../../components/emerge/BuildingOrClinic';
import './ReplacementDesk.board.css';
import { StateBlock as TideState } from '../../components/tide';
import PhonakFormPanel, { phonakFactWords, phonakStage } from './PhonakFormPanel';
import { phonakFormApi, type PhonakFacts, type PhonakRecentItem } from './phonakFormApi';
import {
  manifestApi, dollarsToCents, replacementReasonWords,
  REPLACEMENT_SIDES, REPLACEMENT_REASON_CODES, REPLACEMENT_FINANCIAL_PATHS,
  REPLACEMENT_SIDE_LABEL, REPLACEMENT_REASON_LABEL, REPLACEMENT_PATH_LABEL, REPLACEMENT_NOTE_MAX,
  type Replacement, type ReplacementGate, type ReplacementResidentHit,
  type ReplacementSide, type ReplacementReasonCode, type ReplacementFinancialPath,
} from './manifestApi';

const shortDate = (iso: string | null) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—');

const IDENTIFIED_FROM: Record<string, string> = {
  REQUEST: 'Replacement requested in eMerge',
  MANIFEST: 'Marked REPLACEMENT on a provider manifest',
  EMERGE_CASE: 'eMerge replacement case opened',
  MONDAY_CASE: 'Date on the imported (Monday) replacement case',
};

const MISSING_WORDS: Record<string, string> = {
  WARRANTY_NOT_CHECKED: 'warranty not checked',
  OUT_OF_WARRANTY: 'out of warranty',
  WARRANTY_ENDED: 'warranty has ended',
  PAID_IN_FULL_NOT_CHECKED: 'paid in full not checked',
  NOT_PAID_IN_FULL: 'UMED not paid in full',
};

// The server's refusals, in the words a person acts on. The wrapper keeps the
// code and drops the server's sentence for these routes, so the code is what
// is branched on; anything not named here falls back to what the call says.
const REFUSAL_WORDS: Record<string, string> = {
  REASON_REQUIRED: 'Record why this is a replacement before placing it.',
  CHECKS_NOT_PASSED: 'Warranty and paid in full must both be confirmed before a replacement is placed.',
  FACILITY_PA_ASSIGNMENT_REQUIRED: 'No PA covers this resident\'s facility. Set one in Facility coverage, then place it.',
  NOT_AN_ADVOCATE: 'The PA who covers this facility is no longer a Patient Advocate. Set coverage again in Facility coverage.',
  REPLACEMENT_CASE_CLOSED: 'The replacement case from this warranty check has closed. Record the warranty check again to open a new one.',
  ASSIGNMENT_CONFLICT: 'Someone placed this resident since the list loaded. Refresh to see who.',
  REQUEST_ALREADY_OPEN: 'This resident already has an open replacement request. Replace its reason from the queue.',
  NOTE_REQUIRED: 'Say what the reason is in the note.',
  NOTE_TOO_LONG: 'The note is 1000 characters at most.',
  AMOUNT_INVALID: 'The amount is dollars and cents, or blank.',
  CANCEL_REASON_REQUIRED: 'Say why the request is withdrawn.',
  NOT_FOUND: 'eMerge no longer finds this request. Refresh the list.',
};
const refusal = (r: { code?: string; message?: string; error?: string }, fallback: string) =>
  (r.code && REFUSAL_WORDS[r.code]) || (r.message && r.message !== 'Request failed' ? r.message : null) || r.error || fallback;

const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--muted)', minWidth: 0 };

/** One key per open form, so a double click records one request, not two. */
const newKey = () => {
  const c: any = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : null;
  return c && typeof c.randomUUID === 'function' ? c.randomUUID() : `rr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * The reason, as the business states it (ruling R1): side, why, how it is
 * paid for, an amount if a person has one, and a note (required for Other).
 * Used to add a resident and to record or replace a row's reason.
 */
function ReasonForm({ patientId, label, replace, submitLabel, onDone, onCancel }: {
  patientId: string; label: string; replace: boolean; submitLabel: string; onDone: (msg: string) => void; onCancel: () => void;
}) {
  const [side, setSide] = useState<ReplacementSide | ''>('');
  const [why, setWhy] = useState<ReplacementReasonCode | ''>('');
  const [path, setPath] = useState<ReplacementFinancialPath | ''>('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef(newKey());
  const idBase = `mf-repl-note-${patientId}`;

  const cents = dollarsToCents(amount);
  const noteNeeded = why === 'OTHER';
  const missing: string[] = [];
  if (!side) missing.push('the side');
  if (!why) missing.push('why');
  if (!path) missing.push('how it is paid for');
  if (noteNeeded && !note.trim()) missing.push('a note (Other needs one)');
  if (cents === undefined) missing.push('an amount in dollars, or leave it blank');
  const ready = missing.length === 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || !side || !why || !path || cents === undefined) return;
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.requestReplacement(patientId, {
        side, reason_code: why, financial_path: path, requested_amount_cents: cents, note: note.trim() || null,
        replace, idempotency_key: key.current,
      });
      if (r.success) onDone(`${label}: ${replacementReasonWords({
        id: '', side, reason_code: why, financial_path: path, requested_amount_cents: cents, note: note.trim() || null, requested_at: '', requested_by: null,
      })}.`);
      else setErr(refusal(r, 'Could not record the reason.'));
    } catch { setErr('Could not reach eMerge. Try again.'); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} aria-label={`Reason for ${label}`} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 0' }}>
      <div className="mf-acts" style={{ alignItems: 'flex-end' }}>
        <label style={labelStyle}>
          <span>Side</span>
          <select value={side} onChange={(e) => setSide(e.target.value as ReplacementSide | '')} disabled={busy} required>
            <option value="">Choose…</option>
            {REPLACEMENT_SIDES.map((s) => <option key={s} value={s}>{REPLACEMENT_SIDE_LABEL[s]}</option>)}
          </select>
        </label>
        <label style={labelStyle}>
          <span>Why</span>
          <select value={why} onChange={(e) => setWhy(e.target.value as ReplacementReasonCode | '')} disabled={busy} required>
            <option value="">Choose…</option>
            {REPLACEMENT_REASON_CODES.map((c) => <option key={c} value={c}>{REPLACEMENT_REASON_LABEL[c]}</option>)}
          </select>
        </label>
        <label style={labelStyle}>
          <span>How it is paid for</span>
          <select value={path} onChange={(e) => setPath(e.target.value as ReplacementFinancialPath | '')} disabled={busy} required>
            <option value="">Choose…</option>
            {REPLACEMENT_FINANCIAL_PATHS.map((p) => <option key={p} value={p}>{REPLACEMENT_PATH_LABEL[p]}</option>)}
          </select>
        </label>
        <label style={labelStyle}>
          <span>Amount, if known</span>
          <input style={{ width: 120 }} inputMode="decimal" placeholder="$" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={busy} aria-invalid={cents === undefined} />
        </label>
      </div>
      <label style={labelStyle}>
        <span>{noteNeeded ? 'Note (required for Other)' : 'Note'}</span>
        <textarea
          style={{ minHeight: 56, resize: 'vertical' }} rows={2} maxLength={REPLACEMENT_NOTE_MAX} value={note}
          onChange={(e) => setNote(e.target.value)} disabled={busy} required={noteNeeded} aria-describedby={`${idBase}-count`}
          placeholder="One ear replaced, the other new? Say so here."
        />
      </label>
      <span id={`${idBase}-count`} className="mf-sub num" style={{ alignSelf: 'flex-end', marginTop: -6 }}>{note.length} / {REPLACEMENT_NOTE_MAX}</span>
      {!ready && <span className="mf-sub">Still needed: {missing.join('; ')}.</span>}
      {err && <span className="mf-note warn" role="alert">{err}</span>}
      <span className="mf-acts">
        <button type="submit" className="board-btn board-btn--primary" disabled={!ready || busy}>{busy ? 'Saving…' : submitLabel}</button>
        <button type="button" className="board-btn" onClick={onCancel} disabled={busy}>Cancel</button>
      </span>
    </form>
  );
}

/** "Add a replacement": find any resident, then say why. The resident then appears in the queue. */
function AddReplacement({ onAdded, onClose }: { onAdded: (msg: string) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<ReplacementResidentHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [picked, setPicked] = useState<ReplacementResidentHit | null>(null);
  const [paging, setPaging] = useState({ total: 0, page: 1, limit: 25 });
  const [more, setMore] = useState(false);
  const moreBusy = useRef(false);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { seq.current += 1; moreBusy.current = false; setMore(false); setHits(null); setProblem(null); setSearching(false); setPaging({ total: 0, page: 1, limit: 25 }); return undefined; }
    const mine = ++seq.current;
    moreBusy.current = false;
    setMore(false);
    // The old page count must not outlive the old query: Show more stays hidden until page 1 of this one arrives.
    setPaging({ total: 0, page: 1, limit: 25 });
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await manifestApi.searchReplacementResidents(term);
        if (mine !== seq.current) return;
        if (r.success && r.data) { setHits(r.data.items); setPaging({ total: r.data.total, page: 1, limit: r.data.limit || 25 }); setProblem(null); } else { setHits(null); setProblem(r.message || r.error || 'Could not search.'); }
      } catch { if (mine === seq.current) { setHits(null); setProblem('Could not reach eMerge.'); } } finally { if (mine === seq.current) setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const showMore = async () => {
    if (moreBusy.current) return;
    moreBusy.current = true;
    const mine = seq.current;
    setMore(true);
    try {
      const r = await manifestApi.searchReplacementResidents(q.trim(), paging.page + 1);
      if (mine !== seq.current) return;
      if (r.success && r.data) {
        const next = r.data.items;
        setHits((cur) => [...(cur || []), ...next]);
        setPaging({ total: r.data.total, page: paging.page + 1, limit: r.data.limit || paging.limit });
      } else setProblem(r.message || r.error || 'Could not load more.');
    } catch { if (mine === seq.current) setProblem('Could not reach eMerge.'); } finally { if (mine === seq.current) { moreBusy.current = false; setMore(false); } }
  };

  const who = picked ? (picked.name || `CounselEAR ${picked.counselear_id || 'id not on file'}`) : '';
  return (
    <section className="mf-card" aria-label="Add a replacement" style={{ marginBottom: 14 }}>
      <div className="mf-card-body mf-panel">
        <h3>Add a replacement</h3>
        {!picked && (
          <>
            <p className="mf-sub">Any resident, whether or not a tag or a manifest has put them here. Search by name or CounselEAR id.</p>
            <input style={{ marginTop: 8 }} aria-label="Find a resident by name or CounselEAR id" placeholder="Name or CounselEAR id" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
            {searching && <p className="mf-sub" role="status">Searching…</p>}
            {problem && <p className="mf-note warn" role="alert">{problem}</p>}
            {hits && hits.length === 0 && !searching && <TideState kind="empty" reason="filtered" message={<>No resident matches “{q.trim()}”. Residents with no name on file can only be found by CounselEAR id.</>} />}
            {hits && hits.length > 0 && (
              <ul className="mf-options" style={{ marginTop: 0 }}>
                {hits.map((h) => (
                  <li key={h.id}>
                    <button type="button" disabled={!h.in_place_scope} onClick={() => setPicked(h)}>
                      <strong>{h.name || 'Name not on file'}</strong>
                      <span className="mf-sub"> {h.counselear_id ? `CounselEAR ${h.counselear_id} · ` : ''}<BuildingOrClinic row={h} fallback="No facility on file" /> · {h.situation_words}</span>
                      {h.open_replacement && <span className="mf-sub"> · Already has an open replacement request</span>}
                      {!h.in_place_scope && <span className="mf-sub"> · Outside your buildings</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {hits && hits.length > 0 && paging.total > hits.length && (
              <>
                <p className="mf-sub" role="status">Showing 1–{hits.length} of {paging.total}</p>
                <button type="button" className="board-btn" disabled={more} onClick={showMore}>{more ? 'Loading…' : 'Show more'}</button>
              </>
            )}
            {hits && paging.total > paging.limit && <p className="mf-sub">Type more of the name, or the CounselEAR id, to narrow.</p>}
          </>
        )}
        {picked && (
          <>
            <p className="mf-picked">
              <span>{who}</span>
              <span className="mf-sub"><BuildingOrClinic row={picked} fallback="No facility on file" /></span>
              <button type="button" className="board-btn" onClick={() => setPicked(null)}>Change resident</button>
            </p>
            <ReasonForm
              patientId={picked.id} label={who} replace={false} submitLabel="Add to the queue"
              onDone={(m) => onAdded(`Added ${m}`)} onCancel={onClose}
            />
          </>
        )}
        {!picked && <p className="mf-acts" style={{ marginTop: 8 }}><button type="button" className="board-btn" onClick={onClose}>Close</button></p>}
      </div>
    </section>
  );
}

function CheckLine({ label, gate, row, canCheck, onSaved }: {
  label: string; gate: ReplacementGate; row: Replacement; canCheck: boolean; onSaved: () => void;
}) {
  const c = row.checks[gate];
  const [until, setUntil] = useState<string>(c?.warranty_expires_on || row.warranty_hint.expires_on || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const record = async (result: 'PASS' | 'FAIL') => {
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.recordCheck(row.patient_id, { gate, result, warranty_expires_on: gate === 'WARRANTY' && result === 'PASS' ? until || null : null });
      if (r.success) onSaved(); else setErr(r.message || r.error || 'Could not record the check.');
    } catch { setErr('Could not reach eMerge. Try again.'); } finally { setBusy(false); }
  };
  const said = c
    ? (gate === 'WARRANTY'
      ? (c.result === 'PASS' ? `In warranty until ${shortDate(c.warranty_expires_on)}` : 'Out of warranty')
      : (c.result === 'PASS' ? 'Paid in full' : 'Not paid in full'))
    : 'Not checked';
  return (
    <div className="mf-checkline">
      <span className="mf-sub">
        <strong>{label}:</strong> <span className={c ? (c.result === 'PASS' ? 'mf-ok' : 'mf-bad') : ''}>{said}</span>
        {c && ` — ${c.checked_by || 'someone'}, ${shortDate(c.checked_at)}`}
      </span>
      {gate === 'WARRANTY' && !c && (
        <span className="mf-notes">
          Check CounselEAR → Devices tab.
          {row.dispensed_on && ` Provider sheet: dispensed ${shortDate(row.dispensed_on)}${row.ears ? ` (${row.ears})` : ''}, so about ${shortDate(row.warranty_hint.expires_on)} on the two-year term.`}
        </span>
      )}
      {canCheck && (
        <span className="mf-acts">
          {gate === 'WARRANTY' && (
            <input type="date" aria-label={`Warranty end date for ${residentLabel(row)}`} value={until} onChange={(e) => setUntil(e.target.value)} disabled={busy} />
          )}
          <button type="button" className="board-btn" disabled={busy || (gate === 'WARRANTY' && !until)} onClick={() => record('PASS')}>
            {gate === 'WARRANTY' ? 'In warranty' : 'Paid in full'}
          </button>
          <button type="button" className="board-btn" disabled={busy} onClick={() => record('FAIL')}>
            {gate === 'WARRANTY' ? 'Out of warranty' : 'Not paid'}
          </button>
        </span>
      )}
      {err && <span className="mf-note warn" role="alert">{err}</span>}
    </div>
  );
}

/** The open request's reason, or the plain statement that there is none, and the ways to record, replace or withdraw it. */
function ReasonLine({ row, canPlace, onChanged }: { row: Replacement; canPlace: boolean; onChanged: (msg?: string) => void }) {
  const [mode, setMode] = useState<'none' | 'edit' | 'withdraw'>('none');
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const req = row.request;
  const who = residentLabel(row);

  const withdraw = async () => {
    if (!why.trim()) return;
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.cancelReplacementRequest(row.patient_id, why.trim());
      if (r.success) onChanged(`${who}'s replacement request was withdrawn.`);
      else setErr(refusal(r, 'Could not withdraw the request.'));
    } catch { setErr('Could not reach eMerge. Try again.'); } finally { setBusy(false); }
  };

  return (
    <div className="mf-checkline">
      <span className="mf-sub">
        <strong>Reason:</strong>{' '}
        {req ? <span>{replacementReasonWords(req)}</span> : <span className="mf-bad">No reason recorded — add one</span>}
        {req && req.requested_by && ` — ${req.requested_by}, ${shortDate(req.requested_at)}`}
      </span>
      {canPlace && mode === 'none' && (
        <span className="mf-acts">
          <button type="button" className="board-btn" onClick={() => setMode('edit')} aria-label={`${req ? 'Replace the reason' : 'Add a reason'} for ${who}`}>
            {req ? 'Replace reason' : 'Add a reason'}
          </button>
          {req && <button type="button" className="board-btn" onClick={() => setMode('withdraw')} aria-label={`Withdraw the request for ${who}`}>Withdraw</button>}
        </span>
      )}
      {mode === 'edit' && (
        <ReasonForm
          patientId={row.patient_id} label={who} replace={Boolean(req)} submitLabel={req ? 'Replace the reason' : 'Record the reason'}
          onDone={(m) => { setMode('none'); onChanged(`Reason recorded for ${m}`); }} onCancel={() => setMode('none')}
        />
      )}
      {mode === 'withdraw' && (
        <span className="mf-acts">
          <input aria-label={`Why the request for ${who} is withdrawn`} placeholder="Why is it withdrawn?" maxLength={500} value={why} onChange={(e) => setWhy(e.target.value)} disabled={busy} />
          <button type="button" className="board-btn board-btn--primary" disabled={!why.trim() || busy} onClick={withdraw}>{busy ? 'Withdrawing…' : 'Withdraw the request'}</button>
          <button type="button" className="board-btn" disabled={busy} onClick={() => { setMode('none'); setWhy(''); }}>Keep it</button>
        </span>
      )}
      {err && <span className="mf-note warn" role="alert">{err}</span>}
    </div>
  );
}

/** The Place section of the docked record: who it goes to, why it cannot be placed yet, and the button. */
function PlaceSection({ row, canPlace, onChanged }: { row: Replacement; canPlace: boolean; onChanged: (msg?: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const to = row.goes_to;
  const who = residentLabel(row);

  const place = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.placeReplacement(row.patient_id);
      if (r.success && r.data) onChanged(`${who}'s replacement placed with ${r.data.advocate || 'the PA who covers the facility'}${r.data.opened ? ' (a replacement case was opened)' : ''}.`);
      else setErr(refusal(r, 'Could not place this replacement.'));
    } catch { setErr('Could not reach eMerge. Try again.'); } finally { setBusy(false); }
  };

  return (
    <div className="mf-panel">
      <span className="mf-sub">
        {row.ready
          ? 'Both checks passed.'
          : `Not placeable yet: ${row.missing.map((m) => MISSING_WORDS[m] || m).join('; ')}.`}
      </span>
      {to ? (
        <span className="mf-sub">Goes to: <strong>{to.advocate || 'the covering PA'}</strong> (covers {to.facility_name})</span>
      ) : (
        <span className="mf-note warn">
          {row.facility_id
            ? <>No PA covers {row.facility_name || 'this facility'} — set one in <Link to="?view=coverage">Facility coverage</Link>.</>
            : <>{clinicWords(row) ? `No building is confirmed for this resident (${clinicWords(row)}), so no PA covers them.` : 'No facility is on file for this resident, so no PA covers them.'}</>}
        </span>
      )}
      {canPlace && (
        <span className="mf-acts">
          <button type="button" className="board-btn board-btn--primary" onClick={place} disabled={!row.can_place || busy}>
            {busy ? 'Placing…' : to ? `Place with ${(to.advocate || 'the PA').split(' ')[0]}` : 'Place'}
          </button>
        </span>
      )}
      {err && <span className="mf-note warn" role="alert">{err}</span>}
    </div>
  );
}

/** Everything a row is waiting on, in words: the checks, a reason, a covering PA. */
function waitingWords(r: Replacement): string {
  const w = r.missing.map((m) => MISSING_WORDS[m] || m);
  if (!r.request) w.push('no reason recorded');
  if (!r.goes_to) w.push(r.facility_id ? 'no PA covers the building' : 'no building on file');
  return w.length ? w.join('; ') : 'not cleared to place';
}

function ReadyChip({ r }: { r: Replacement }) {
  if (r.can_place) return <ToneChip tone="ok">Ready to place</ToneChip>;
  const words = waitingWords(r);
  return <ToneChip tone="attn" title={`Waiting: ${words}`}><span className="mf-repl-clip">Waiting: {words}</span></ToneChip>;
}

function ReasonCell({ r }: { r: Replacement }) {
  if (!r.request) return <ToneChip tone="stop">No reason recorded</ToneChip>;
  const w = replacementReasonWords(r.request);
  return <span className="board-clip" title={w}>{w}</span>;
}

function WarrantyCell({ r }: { r: Replacement }) {
  const c = r.checks.WARRANTY;
  if (!c) return <ToneChip tone="quiet">Not checked</ToneChip>;
  return c.result === 'PASS'
    ? <ToneChip tone="ok">In warranty until {shortDate(c.warranty_expires_on)}</ToneChip>
    : <ToneChip tone="stop">Out of warranty</ToneChip>;
}

function UmedCell({ r }: { r: Replacement }) {
  const c = r.checks.PAID_IN_FULL;
  if (!c) return <ToneChip tone="quiet">Not checked</ToneChip>;
  return c.result === 'PASS'
    ? <FilledStatusChip word="PAID IN FULL" column={UMED_STATUS} label="Paid in full" size="sm" />
    : <ToneChip tone="stop">Not paid in full</ToneChip>;
}

/** The Phonak courtesy form's stage for a row, read-only. A review send is not shown as reaching Phonak. */
function PhonakCell({ r, facts }: { r: Replacement; facts: Map<string, PhonakFacts> | null }) {
  // No request, or the facts could not be read (no permission, or a failure): nothing is said.
  if (!r.request || !facts) return <span className="board-na">—</span>;
  const f = facts.get(r.request.id);
  const stage = phonakStage(f);
  if (stage === 'sent') {
    const words = phonakFactWords(f) || '';
    const reachedPhonak = Boolean(f && f.last_live && f.last_live.outcome === 'SENT');
    return <ToneChip tone={reachedPhonak ? 'ok' : 'attn'} title={words}><span className="mf-repl-clip">{words}</span></ToneChip>;
  }
  if (stage === 'signed') return <ToneChip tone="quiet">Signed</ToneChip>;
  if (stage === 'prepared') return <ToneChip tone="quiet">Prepared</ToneChip>;
  return <ToneChip tone="quiet">Not prepared</ToneChip>;
}

/** Placed requests with their Phonak facts: placing takes a resident off the desk, and the form usually follows. */
function PlacedPhonakList({ items, onChanged }: { items: PhonakRecentItem[]; onChanged: (msg?: string) => void }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const placed = items.filter((i) => i.state === 'PLACED');
  return (
    <section className="mf-card" aria-label="Phonak forms for placed replacements" style={{ marginBottom: 14 }}>
      <div className="mf-card-body mf-panel">
        <h3>Phonak forms (placed)</h3>
        <p className="mf-sub">Replacements placed in the last 45 days. Placing takes a resident off the desk; the Phonak form usually follows.</p>
        {placed.length === 0 && <p className="mf-sub">No placed replacement in the last 45 days.</p>}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {placed.map((i) => {
            const name = residentName(i) || 'Name not on file';
            const open = openId === i.request_id;
            const words = phonakFactWords(i.phonak);
            const stage = phonakStage(i.phonak);
            return (
              <li key={i.request_id}>
                <div className="mf-acts" style={{ alignItems: 'center' }}>
                  <strong>{name}</strong>
                  <span className="mf-sub">{i.facility_name || 'No facility on file'} · placed {shortDate(i.placed_at)}</span>
                  <ToneChip tone={stage === 'sent' ? (i.phonak.last_live && i.phonak.last_live.outcome === 'SENT' ? 'ok' : 'attn') : 'quiet'}>
                    {words || (stage === 'signed' ? 'Signed' : stage === 'prepared' ? 'Prepared' : 'Not prepared')}
                  </ToneChip>
                  <button type="button" className="board-btn" aria-expanded={open} aria-label={`${open ? 'Close' : 'Open'} the Phonak form for ${name}`} onClick={() => setOpenId(open ? null : i.request_id)}>
                    {open ? 'Close' : 'Phonak form'}
                  </button>
                </div>
                {open && <PhonakFormPanel requestId={i.request_id} onChanged={onChanged} />}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function GoesToCell({ r }: { r: Replacement }) {
  const to = r.goes_to;
  return (
    <span className="mf-cellstack">
      {to
        ? <span className="board-clip" title={`${to.advocate || 'the covering PA'} (covers ${to.facility_name})`}>{to.advocate || 'the covering PA'}</span>
        : <ToneChip tone="stop">{r.facility_id ? 'No PA covers this building' : (clinicWords(r) ? `${clinicWords(r)} · no PA yet` : 'No building on file')}</ToneChip>}
      {r.holder && r.holder_id !== to?.advocate_user_id && <ToneChip tone="attn" title={`${r.holder} holds this resident now`}><span className="mf-repl-clip">Held by {r.holder}</span></ToneChip>}
    </span>
  );
}

type LegendKey = 'ready' | 'checks' | 'noreason';
const LEGEND_TEST: Record<LegendKey, (r: Replacement) => boolean> = {
  ready: (r) => r.can_place,
  checks: (r) => !r.ready,
  noreason: (r) => !r.request,
};

// Sorts of the rows the desk holds. The default order is the server's: building A to Z.
const SORTERS: Record<string, (r: Replacement) => string | null> = {
  resident: (r) => residentName(r),
  building: (r) => r.facility_name,
  manifest: (r) => r.on_manifest,
  identified: (r) => r.identified_on ?? null,
};

export default function ReplacementDesk() {
  const [items, setItems] = useState<Replacement[] | null>(null);
  const [can, setCan] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [legendKey, setLegendKey] = useState<LegendKey | null>(null);
  const [open, setOpen] = useOpenParam('open');
  const [phonakItems, setPhonakItems] = useState<PhonakRecentItem[] | null>(null);
  const [showPlaced, setShowPlaced] = useState(false);

  // The Phonak facts are a read-only column. A failure or a 403 shows nothing, never an error.
  const loadPhonak = useCallback(async () => {
    try {
      const r = await phonakFormApi.recent();
      setPhonakItems(r && r.success && r.data && Array.isArray(r.data.items) ? r.data.items : null);
    } catch { setPhonakItems(null); }
  }, []);
  useEffect(() => { loadPhonak(); }, [loadPhonak]);
  const phonakFacts = useMemo(() => (phonakItems ? new Map(phonakItems.map((i) => [i.request_id, i.phonak] as [string, PhonakFacts])) : null), [phonakItems]);

  const load = useCallback(async () => {
    setProblem(null);
    try {
      const r = await manifestApi.replacements();
      if (r.success && r.data) { setItems(r.data.items); setCan(r.data.canPlace); }
      else setProblem(r.message || r.error || 'Could not load the replacements.');
    } catch { setProblem('Could not reach eMerge.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const matching = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (items || []).filter((r) => !q || `${residentLabel(r)} ${buildingWords(r, '')}`.toLowerCase().includes(q));
  }, [items, filter]);
  const shown = useMemo(() => (legendKey ? matching.filter(LEGEND_TEST[legendKey]) : matching), [matching, legendKey]);
  const sorted = useClientSort(shown, SORTERS);

  const onChanged = (m?: string) => { if (m) setNotice(m); load(); loadPhonak(); };

  const legendItems: MfLegendItem[] = useMemo(() => {
    const first = (k: LegendKey) => matching.find(LEGEND_TEST[k]);
    const sampleFor = (k: LegendKey) => first(k);
    const waitSample = sampleFor('checks');
    return [
      { key: 'ready', label: 'Ready to place', sample: <ToneChip tone="ok">Ready to place</ToneChip>, meaning: 'Both checks passed, a reason is recorded and a PA covers the building. The server decides again on placing.', count: matching.filter(LEGEND_TEST.ready).length },
      { key: 'checks', label: 'Waiting on checks', sample: waitSample ? <ReadyChip r={waitSample} /> : <ToneChip tone="attn">Waiting: warranty not checked</ToneChip>, meaning: 'Warranty or paid in full is not confirmed yet. The chip says what is still missing.', count: matching.filter(LEGEND_TEST.checks).length },
      { key: 'noreason', label: 'No reason recorded', sample: <ToneChip tone="stop">No reason recorded</ToneChip>, meaning: 'Nobody has said why this is a replacement. A replacement cannot be placed without one.', count: matching.filter(LEGEND_TEST.noreason).length },
    ];
  }, [matching]);

  const ready = (items || []).filter((r) => r.can_place).length;

  const columns: BoardColumn<Replacement>[] = [
    { key: 'building', label: 'Building', sort: 'building', help: 'The resident\'s building. The list starts in the server\'s order: building A to Z.', render: (r) => (r.facility_name ? <span className="board-clip" title={r.facility_name}>{r.facility_name}</span> : <BuildingOrClinic row={r} fallback={<span className="board-na">no building on file</span>} />) },
    { key: 'reason', label: 'Reason', help: 'Why this is a replacement: side, reason, how it is paid for, an amount if a person typed one, and a note. Placing needs one.', render: (r) => <ReasonCell r={r} /> },
    { key: 'warranty', label: 'Warranty', help: 'Whether someone confirmed the device is in warranty (CounselEAR, Devices tab).', render: (r) => <WarrantyCell r={r} /> },
    { key: 'umed', label: 'Initial UMED', help: 'Whether someone confirmed the initial UMED is paid in full.', render: (r) => <UmedCell r={r} /> },
    { key: 'ready', label: 'Ready', help: 'Ready to place when both checks passed, a reason is recorded and a PA covers the building; otherwise what it is waiting on.', render: (r) => <ReadyChip r={r} /> },
    { key: 'goes', label: 'Goes to', help: 'The PA who covers the resident\'s building. Nobody is chosen here. If someone else holds the resident now, that is said as information.', render: (r) => <GoesToCell r={r} /> },
    { key: 'phonak', label: 'Phonak form', help: 'Whether the Phonak One-Time Courtesy Replacement form was prepared, signed and sent (review or live). Read-only.', render: (r) => <PhonakCell r={r} facts={phonakFacts} /> },
    { key: 'case', label: 'Case', help: 'Whether a replacement case is open. One opens when the resident is placed.', render: (r) => <ToneChip tone="quiet">{r.case_id ? 'Case open' : 'Opens when placed'}</ToneChip> },
    { key: 'ce', label: 'CounselEAR', help: 'The resident\'s CounselEAR tags.', render: (r) => (r.counselear_tags ? <span className="board-clip" title={r.counselear_tags}>{r.counselear_tags}</span> : <span className="board-na">none</span>) },
    { key: 'identified', label: 'Identified', sort: 'identified', help: 'The date eMerge learned this resident needs a replacement: the earliest of the request recorded here, the manifest marked REPLACEMENT and the eMerge replacement case; only if none exists, the date on the imported replacement case. Not recorded when there is none.', render: (r) => (r.identified_on ? <span title={(r.identified_source && IDENTIFIED_FROM[r.identified_source]) || undefined}>{shortDate(r.identified_on)}</span> : <span className="board-na">Not recorded</span>) },
    { key: 'manifest', label: 'On manifest', sort: 'manifest', help: 'The date the replacement appeared on a provider manifest.', render: (r) => (r.on_manifest ? <span>{shortDate(r.on_manifest)}</span> : <span className="board-na">not on a manifest</span>) },
  ];

  const identity: BoardIdentity<Replacement> = {
    label: 'Resident',
    sort: 'resident',
    help: 'A resident waiting for a replacement. Open the row to place it, record why, and record the warranty and paid-in-full checks, docked beside the table.',
    render: (r) => <ResidentName p={r} />,
    name: (r) => residentLabel(r),
  };

  const sections = (r: Replacement): BoardDetailSection[] => [
    { id: 'place', label: 'Place', render: () => <PlaceSection row={r} canPlace={can} onChanged={onChanged} /> },
    { id: 'reason', label: 'Reason', render: () => <div className="mf-panel"><ReasonLine row={r} canPlace={can} onChanged={onChanged} /></div> },
    {
      id: 'checks', label: 'Checks',
      render: () => (
        <div className="mf-panel">
          <CheckLine label="Warranty" gate="WARRANTY" row={r} canCheck={can} onSaved={() => onChanged()} />
          <CheckLine label="Initial UMED" gate="PAID_IN_FULL" row={r} canCheck={can} onSaved={() => onChanged()} />
        </div>
      ),
    },
    ...(can && r.request
      ? [{ id: 'phonak', label: 'Phonak form', defaultCollapsed: true, render: () => <PhonakFormPanel requestId={r.request!.id} onChanged={onChanged} /> }]
      : []),
    {
      id: 'details', label: 'Details',
      render: () => {
        const to = r.goes_to;
        return (
          <div className="mf-panel">
            <dl className="mf-facts">
              <dt>Held now by</dt>
              <dd>
                {r.holder
                  ? (r.holder_id !== to?.advocate_user_id
                    ? `${r.holder} holds this resident now — placing moves all ${r.open_cases} open ${r.open_cases === 1 ? 'record' : 'records'}${to ? ` to ${to.advocate || 'the covering PA'}` : ''}`
                    : r.holder)
                  : <span className="board-na">nobody</span>}
              </dd>
              <dt>Open records</dt><dd>{r.open_cases}</dd>
              <dt>Warranty hint</dt>
              <dd>{r.warranty_hint.expires_on ? `about ${shortDate(r.warranty_hint.expires_on)} on the two-year term` : <span className="board-na">no provider sheet date</span>}</dd>
              <dt>Dispensed</dt>
              <dd>{r.dispensed_on ? `${shortDate(r.dispensed_on)}${r.ears ? ` (${r.ears})` : ''}` : <span className="board-na">not on a provider sheet</span>}</dd>
              <dt>CounselEAR tags</dt><dd>{r.counselear_tags || <span className="board-na">none</span>}</dd>
            </dl>
          </div>
        );
      },
    },
  ];

  let state: React.ReactNode = null;
  if (problem) state = <MfLoadFailure message={problem} retry={load} />;
  else if (!items) state = <MfSkeleton bare />;
  else if (items.length === 0) state = <TideState kind="empty" reason="none" kindLabel="Nothing waiting" message="No resident is waiting for a replacement." />;
  else if (sorted.rows.length === 0) {
    state = (
      <div className="mf-panel">
        <p>No replacement matches this search or key.</p>
        <div className="mf-acts">
          {filter && <button type="button" className="board-btn" onClick={() => setFilter('')}>Clear search</button>}
          {legendKey && <button type="button" className="board-btn" onClick={() => setLegendKey(null)}>Show all</button>}
        </div>
      </div>
    );
  }

  const notices = (notice || adding || showPlaced || (!can && items)) ? (
    <>
      {notice && <p className="mf-notice" role="status">{notice}</p>}
      {!can && items && <p className="mf-notice">You can see these residents; adding, checking and placing them needs the lead-placement permission.</p>}
      {showPlaced && can && <PlacedPhonakList items={phonakItems || []} onChanged={() => { loadPhonak(); }} />}
      {adding && <AddReplacement onClose={() => setAdding(false)} onAdded={(m) => { setAdding(false); setNotice(m); load(); }} />}
    </>
  ) : null;

  return (
    <main className="mf-main mf-main--board">
      <MfBoard<Replacement>
        title="Replacements"
        count={items ? items.length : null}
        units={['resident', 'residents']}
        what={<>
          A replacement says why, and is placed only after someone confirms it is in warranty (CounselEAR, Devices tab) and the initial UMED is paid in full. It goes to the PA who covers the resident&apos;s building.
          {items && ` ${ready} ready to place.`}
          {' '}Identified is the date eMerge learned the resident needs a replacement: the earliest of the request recorded here, the manifest marked REPLACEMENT and the eMerge replacement case (the imported Monday case date is used only when none of those exists). It says Not recorded when eMerge has none.
        </>}
        actions={can ? (
          <>
            {!adding && <button type="button" className="board-btn board-btn--primary" onClick={() => { setAdding(true); setNotice(null); }}>Add a replacement</button>}
            <button type="button" className="board-btn" aria-expanded={showPlaced} onClick={() => setShowPlaced((v) => !v)}>Phonak forms (placed)</button>
          </>
        ) : null}
        notice={notices}
        controls={items && items.length > 0 ? (
          <input type="search" aria-label="Find a resident or facility" placeholder="Find a resident or facility" value={filter} onChange={(e) => setFilter(e.target.value)} />
        ) : null}
        legend={items && items.length > 0 ? (
          <MfLegend title="Where each replacement stands" items={legendItems} whole={matching.length} wholeWords="in this view" units={['resident', 'residents']}
            value={legendKey} onSelect={(k) => setLegendKey(k as LegendKey | null)}
            scope="A resident can carry more than one of these at once, so the counts overlap. Press an entry to show only those residents." />
        ) : null}
        state={state}
        columns={columns}
        identity={identity}
        rows={sorted.rows}
        rowKey={(r) => r.patient_id}
        openKey={open}
        onOpenChange={setOpen}
        detailSections={sections}
        detailLabel={(r) => `${residentLabel(r)}: the replacement`}
        sort={sorted.sort}
        dir={sorted.dir}
        sorts={sorted.sorts}
        onSort={sorted.onSort}
        notes={['"Name not recorded": eMerge holds no name for this resident yet; CounselEAR sends the name after the resident.']}
      />
    </main>
  );
}
